import { useRef, useState } from 'react'

import { Button, Flex, Input, Text, VStack } from '@chakra-ui/react'
import { EditorHeaderSlot } from '@editor/EditorProvider'
import { AddIcon } from '@editor/assets/icons'
import LoadingPage from '@editor/components/LoadingPage'
import PageErrorHandler from '@editor/components/PageErrorHandler'
import { USER_TYPES } from '@editor/config/constants'
import { useUploadPcd } from '@editor/hooks/uploadPcd'
import { FileProcessingStatus } from '@editor/interfaces/file'
import { ERROR_PROCESS, getErrorText } from '@editor/services/ErrorHandler'
import { decideActionPermission } from '@editor/services/Validation'
import { RootState, useAppDispatch } from '@editor/store/app'
import { patchInspectionAreas } from '@editor/store/page'
import * as Sentry from '@sentry/react'
import { isMobile } from 'react-device-detect'
import { useSelector } from 'react-redux'

const MissingPCDFIleRedirect = ({
  pageName = '3D画面',
  forceFileProcessingLoading = false,
}: {
  pageName?: string
  forceFileProcessingLoading?: boolean
}) => {
  const { processSelectedFile, uploadPcd } = useUploadPcd()

  // Store
  const dispatch = useAppDispatch()
  const project = useSelector((state: RootState) => state.page.project)
  const inspectionArea = useSelector((state: RootState) => state.page.inspectionArea)
  const userType = useSelector((state: RootState) => state.user.userType) as keyof typeof USER_TYPES
  const isOwner = useSelector((state: RootState) => state.page.isOwner)
  const isInvited = useSelector((state: RootState) => state.page.isInvited)

  // Permissions
  const isAllowedToModify = decideActionPermission(isOwner, isInvited).PROJECT_DASHBOARD.MODIFY.includes(userType)

  // File upload
  const uploadFileRef = useRef<HTMLInputElement>(null)
  const [isUploading, setIsUploading] = useState(false)
  const [uploadProgress, setUploadProgress] = useState<number | null>(null)

  const getText = () => {
    if (!isAllowedToModify) {
      return '点群ファイルがまだ追加されていません。'
    }

    if (inspectionArea?.downsample_status?.status === FileProcessingStatus.FAILED) {
      return (
        <>
          {getErrorText(
            ERROR_PROCESS.CREATE_INSPECTION_AREA,
            inspectionArea?.downsample_status?.status_id,
            '点群ファイルの変換に失敗しました。',
          )}
          <br />
          もう一度ファイルをアップロードしてください。
        </>
      )
    }

    return isUploading
      ? `アップロード中...${uploadProgress !== null ? `${uploadProgress}%` : ''}`
      : `${pageName}を利用するためには点群ファイルを追加してください`
  }

  // Down-sampling is still running
  if (
    (inspectionArea &&
      (inspectionArea.downsample_status?.status === FileProcessingStatus.RUNNING ||
        inspectionArea.mesh_bundle_status?.status === FileProcessingStatus.RUNNING)) ||
    forceFileProcessingLoading
  ) {
    return (
      <PageErrorHandler>
        <VStack p={0} m={0} h="100svh" gap={0}>
          <EditorHeaderSlot />
          <Flex
            bg="black"
            flex={1}
            w="100%"
            color="white"
            alignItems="center"
            justifyContent="center"
            flexDirection="column"
            gap={3}
          >
            <LoadingPage text="ファイルを変換中..." />
          </Flex>
        </VStack>
      </PageErrorHandler>
    )
  }

  return (
    <PageErrorHandler>
      <VStack p={0} m={0} h="100svh" gap={0}>
        <EditorHeaderSlot />
        <Flex
          bg="black"
          flex={1}
          w="100%"
          color="white"
          alignItems="center"
          justifyContent="center"
          flexDirection="column"
          gap={3}
        >
          <Text fontSize="lg" color="gray2.300" textAlign="center">
            {getText()}
          </Text>
          {isAllowedToModify && (
            <>
              <Button
                variant="outline"
                color="gray2.100"
                borderColor="gray2.300"
                _hover={{ color: 'gray2.800', backgroundColor: 'white' }}
                size="lg"
                onClick={() => uploadFileRef?.current?.click()}
                disabled={isUploading}
              >
                <AddIcon />
                点群ファイルをアップロード
              </Button>
              <Input
                data-testid="file-upload"
                hidden
                ref={uploadFileRef}
                type="file"
                accept={isMobile ? '' : '.las,.laz,.ply'}
                onChange={async (e) => {
                  e.preventDefault()
                  if (!project || !inspectionArea) return

                  const file = await processSelectedFile(e)
                  if (!file) return

                  // start uploading
                  setIsUploading(true)
                  try {
                    const area = await uploadPcd(project, inspectionArea, file, setUploadProgress)

                    // need to update both unfortunately
                    // dispatch(setInspectionArea(area))
                    dispatch(patchInspectionAreas([area]))
                  } catch (err) {
                    Sentry.captureException(err)
                  } finally {
                    setIsUploading(false)
                    setUploadProgress(null)
                  }
                }}
              />
            </>
          )}
        </Flex>
      </VStack>
    </PageErrorHandler>
  )
}

export default MissingPCDFIleRedirect
